/**
 * ------------------------------------------------------------
 *  Zapiet Eats – Pickup & Delivery Cutoff Enforcement
 *  Author: Mario Sanchez (EIT – E-commerce & Web Systems)
 *  Date & Time of Completion: Nov 19, 2025 at 3:16AM
 * ------------------------------------------------------------
 *
 *  This unified script handles two separate cutoff behaviors for:
 *
 *    1. STORE PICKUP   → STRICT ENFORCEMENT
 *    2. DELIVERY       → WARNING-ONLY (NON-BLOCKING)
 *
 *  The data source for cutoff times is a Shopify-hosted JSON file:
 *    /pickup-cutoff2.json
 *
 * ------------------------------------------------------------
 *  PICKUP MODE  (STRICT)
 * ------------------------------------------------------------
 *  - Immediately identifies the selected restaurant from the widget
 *  - Disables invalid time slots inside the Zapiet calendar modal
 *  - Ensures the "Confirm" button is disabled when:
 *        • No date selected
 *        • No time selected
 *        • Selected time is outside allowed open–cutoff window
 *  - Re-enables Confirm only when a valid time is selected
 *  - Does NOT depend on Zapiet’s top bar
 *  - Works before the widget is submitted (real-time validation)
 *
 *  The Pickup logic must remain PURE and NOT affected by Delivery logic.
 *
 * ------------------------------------------------------------
 *  DELIVERY MODE  (WARNING-ONLY)
 * ------------------------------------------------------------
 *  - Relies on Zapiet’s top bar after widget submission because:
 *      Zapiet Eats only finalizes:
 *        • assigned store
 *        • distance
 *        • delivery date & time
 *        • fees
 *      AFTER the user clicks the final "Continue" button inside the widget
 *
 *  - If the assigned delivery time is outside cutoff:
 *        • Shows a Conti’s-style warning modal
 *        • Opens the widget automatically once
 *        • Allows the user to simply close the modal (“I'll change it”)
 *        • Does NOT enforce re-selection
 *        • Does NOT disable any Zapiet controls
 *        • Does NOT fight Zapiet’s internal logic
 *
 *  - Warning modal appears only once per invalid state
 *    (userDismissedOnce prevents looping)
 *
 * ------------------------------------------------------------
 *  ARCHITECTURE NOTES
 * ------------------------------------------------------------
 *  • Pickup logic and Delivery logic are triggered from the same
 *    script but separated by ON-MODE condition:
 *
 *        if (currentMode === "pickup")   → run Pickup logic
 *        if (currentMode === "delivery") → run Delivery logic
 *
 *  • Mode is detected using Zapiet Eats tab buttons:
 *        <button id="pickup">      → Pickup mode
 *        <button id="delivery">    → Delivery mode
 *
 *  • Both logics share:
 *        - JSON loading
 *        - store lookup
 *        - date/time parsing
 *        - month mapping
 *
 *  • Pickup behavior MUST NOT be altered by Delivery behavior.
 *  • Delivery behavior MUST NOT enforce cutoff — only warn.
 *
 * ------------------------------------------------------------
 *  PURPOSE
 * ------------------------------------------------------------
 *  This script ensures:
 *
 *   ✔ Pickup times obey strict per-store cutoff rules  
 *   ✔ Delivery times show a user-friendly warning  
 *   ✔ No interference between Pickup & Delivery modes  
 *   ✔ Smooth user experience consistent with Conti’s UX standards  
 *
 * ------------------------------------------------------------
 *  MAINTENANCE NOTES
 * ------------------------------------------------------------
 *  - JSON structure must contain:
 *        [{ restaurant, aliases[], rules[{ date, pickup:{}, delivery:{} }] }]
 *
 *  - Changing Zapiet UI class names may break selectors
 *  - Keep Pickup and Delivery logic isolated inside condition blocks
 *
 * ------------------------------------------------------------
 */



/**
 * ------------------------------------------------------------
 *  Zapiet Eats – Pickup & Delivery Cutoff Enforcement
 *  Author: Mario Sanchez (EIT – E-commerce & Web Systems)
 *  Date & Time of Completion: Nov 19, 2025 at 3:16AM
 * ------------------------------------------------------------
 *
 *  This unified script handles two separate cutoff behaviors for:
 *
 *    1. STORE PICKUP   → STRICT ENFORCEMENT
 *    2. DELIVERY       → WARNING-ONLY (NON-BLOCKING)
 *
 *  The data source for cutoff times is a Shopify theme-asset JSON file:
 *    /assets/pickup-cutoff.json?v=###
 *
 *  Version number is manually controlled to force cache-refresh.
 * ------------------------------------------------------------
 */

(function () {
  "use strict";

  // ---------------------
  // Shared: Load cutoff JSON once (theme asset + manual cache buster)
  // ---------------------

  const JSON_VERSION = "v=117"; // bump this whenever JSON is updated
  const JSON_URL = "//www.contis.ph/cdn/shop/t/109/assets/pickup-cutoff.json?v=182615450207582133901764935574?" + JSON_VERSION;

  let stores = [];

  fetch(JSON_URL, { cache: "no-store" })
    .then(r => r.ok ? r.json() : Promise.reject("Cutoff JSON fetch failed"))
    .then(j => {
      stores = Array.isArray(j) ? j : [];
      console.log("[EIT-DELIVERY] cutoff JSON loaded (version:", JSON_VERSION, ")", stores.length);
    })
    .catch(e => console.warn("[EIT-DELIVERY] failed to load cutoff JSON:", e));

  const months = { Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12 };

  function normalize(s){ return (s || "").replace(/\s+/g, " ").trim(); }

  function escapeHtml(s){
    return String(s || "").replace(/[&<>"']/g, (m) => ({
      "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"
    }[m]));
  }

  function parseISOFromLabel(label){
    if(!label) return null;
    const m = label.match(/(\d{1,2})(?:st|nd|rd|th)?\s+(\w+)/i);
    if(!m) return null;
    const day = String(parseInt(m[1],10)).padStart(2,"0");
    const mon = months[(m[2]||"").slice(0,3)];
    if(!mon) return null;
    return `${new Date().getFullYear()}-${String(mon).padStart(2,"0")}-${day}`;
  }

  function parseTime(str){
    if(!str) return null;
    const m = str.match(/(\d+)\s*:\s*(\d+)\s*(AM|PM)/i);
    if(!m) return null;
    let h = parseInt(m[1],10), mins = parseInt(m[2],10), ap = m[3].toUpperCase();
    if(ap === "PM" && h !== 12) h += 12;
    if(ap === "AM" && h === 12) h = 0;
    return h + mins/60;
  }

  function parseTimeDecimal(str){
    if(!str) return null;
    const m = str.match(/(\d{1,2})\s*:?\s*(\d{2})\s*(AM|PM)/i);
    if(!m) return null;
    let h = parseInt(m[1],10), mins = parseInt(m[2],10);
    const ap = m[3].toUpperCase();
    if(ap==="PM" && h!==12) h+=12;
    if(ap==="AM" && h===12) h=0;
    return h + mins/60;
  }

  function findStoreByName(name){
    const n = normalize(name || "");
    if(!n) return null;
    let exact = stores.find(s => normalize(s.restaurant) === n);
    if(exact) return exact;
    return stores.find(s => Array.isArray(s.aliases) && s.aliases.some(a => n.includes(normalize(a)))) || null;
  }

  // ---------------------
  // Mode detection
  // ---------------------

  function getActiveMode(){
    const pickupBtn = document.querySelector("#pickup");
    const deliveryBtn = document.querySelector("#delivery");

    try {
      if(pickupBtn && (pickupBtn.classList.contains("ZapietEats-DeliveryOption--active") || pickupBtn.getAttribute("aria-selected")==="true"))
        return "pickup";
      if(deliveryBtn && (deliveryBtn.classList.contains("ZapietEats-DeliveryOption--active") || deliveryBtn.getAttribute("aria-selected")==="true"))
        return "delivery";
    } catch(e){}

    const top = document.querySelector("[class*='TopBar'], #ZapietEats-TopBar");
    if(top){
      const t = (top.innerText||"").toLowerCase();
      if(t.includes("pickup")) return "pickup";
      if(t.includes("delivery")) return "delivery";
    }
    return null;
  }

  function onModeChange(cb){
    const container = document.querySelector(".ZapietEats-DeliveryOptions") || document.body;
    container.addaddEventListener("click", () => setTimeout(cb, 40), true);

    const pickupBtn = document.querySelector("#pickup");
    const deliveryBtn = document.querySelector("#delivery");
    [pickupBtn, deliveryBtn].forEach(el => {
      if(!el) return;
      new MutationObserver(() => setTimeout(cb, 40))
        .observe(el, { attributes:true, attributeFilter:["class","aria-selected"] });
    });
  }

  // ---------------------
  // PICKUP MODULE (unchanged)
  // ---------------------

  const PickupModule = (function(){
    function getCalendarConfirmButton(modal){
      if(!modal) return null;
      const primaries = Array.from(modal.querySelectorAll("button.ZapietButton.ZapietButton--primary"));
      return primaries.find(b => (b.textContent||"").trim().toLowerCase() === "confirm") || null;
    }

    function getWidgetContinueButton(){
      const all = Array.from(document.querySelectorAll("button.ZapietButton.ZapietButton--primary"));
      return all.find(b => (b.textContent||"").trim().toLowerCase() === "continue") || null;
    }

    function readTimeButton(modal){
      const btn = modal.querySelector(".Zapiet-Calendar__TimePicker__Button");
      if(!btn) return null;
      const txt = (btn.textContent||"").trim();
      if(!txt) return null;
      return txt;
    }

    function enforceCutoff(){
      if(getActiveMode() !== "pickup") return;

      const modal = document.getElementById("ZapietEats-WidgetModal");
      if(!modal) return;

      const calConfirm = getCalendarConfirmButton(modal);
      const widgetContinue = getWidgetContinueButton();

      const restaurantName =
        window.ZapietEats?.getSelectedRestaurant?.()?.name ||
        document.querySelector(".ZapietDatePicker__Name")?.textContent?.trim() || "";

      const store = findStoreByName(restaurantName);

      if(!store){
        if(calConfirm){ calConfirm.disabled = false; calConfirm.style.opacity = ""; }
        return;
      }

      const selectedDateEl = modal.querySelector(".Zapiet-Calendar__Date--selected");

      if(!selectedDateEl){
        if(calConfirm){ calConfirm.disabled = true; calConfirm.style.opacity = "0.4"; }
        return;
      }

      const iso = (function(label){
        if(!label) return null;
        const m = label.match(/(\d{1,2})(?:st|nd|rd|th)?\s+(\w+)/i);
        if(!m) return null;
        const day = String(parseInt(m[1],10)).padStart(2,"0");
        const month = months[m[2].slice(0,3)] || null;
        if(!month) return null;
        const year = new Date().getFullYear();
        return `${year}-${String(month).padStart(2,"0")}-${day}`;
      })(selectedDateEl.getAttribute("aria-label"));

      if(!iso){
        if(calConfirm){ calConfirm.disabled = true; calConfirm.style.opacity = "0.4"; }
        return;
      }

      const rule = store.rules.find(r => r.date === iso);

      if(!rule){
        if(calConfirm){ calConfirm.disabled = false; calConfirm.style.opacity = ""; }
        return;
      }

      const open = rule.pickup.open;
      const cutoff = rule.pickup.cutoff;

      const timeItems = modal.querySelectorAll(".Zapiet-Calendar__TimePicker__List__Item");
      timeItems.forEach(item => {
        const span = item.querySelector("span");
        const t = span ? parseTime(span.textContent.trim()) : null;
        if(t == null){
          item.style.opacity="";
          item.style.pointerEvents="";
          return;
        }
        if(t < open || t > cutoff){
          item.style.opacity="0.4";
          item.style.pointerEvents="none";
        } else {
          item.style.opacity="";
          item.style.pointerEvents="";
        }
      });

      const timeBtnText = readTimeButton(modal);

      if(!timeBtnText){
        if(calConfirm){ calConfirm.disabled = true; calConfirm.style.opacity = "0.4"; }
        return;
      }

      const tVal = parseTime(timeBtnText);

      if(tVal == null || tVal < open || tVal > cutoff){
        if(calConfirm){ calConfirm.disabled = true; calConfirm.style.opacity = "0.4"; }
      } else {
        if(calConfirm){ calConfirm.disabled = false; calConfirm.style.opacity = ""; }
      }

      timeItems.forEach(item => {
        item.addEventListener("click", () => {
          const span = item.querySelector("span");
          const t = span ? parseTime(span.textContent.trim()) : null;
          if(t != null && t >= open && t <= cutoff){
            if(calConfirm){ calConfirm.disabled = false; calConfirm.style.opacity = ""; }
          } else {
            if(calConfirm){ calConfirm.disabled = true; calConfirm.style.opacity = "0.4"; }
          }
        }, { capture: true, passive: true });
      });

      if(calConfirm && widgetContinue && !calConfirm._eit_confirm_hooked){
        calConfirm.addEventListener("click", () => {
          setTimeout(() => {
            try { widgetContinue.disabled = false; widgetContinue.style.opacity = ""; } catch(e){}
          }, 50);
        });
        calConfirm._eit_confirm_hooked = true;
      }
    }

    function initWatcher(){
      const modal = document.getElementById("ZapietEats-WidgetModal");
      if(!modal) return setTimeout(initWatcher, 200);
      const obs = new MutationObserver(() => {
        setTimeout(enforceCutoff, 60);
      });
      obs.observe(modal, { childList:true, subtree:true, attributes:true });
      modal.addEventListener("click", () => setTimeout(enforceCutoff, 60), true);
      setTimeout(enforceCutoff, 150);
    }

    return { initWatcher, enforceCutoff };
  })();


  // ---------------------
  // DELIVERY MODULE — POP-UP MODAL (CART-STYLE TEXT + OLD POPUP VISUAL)
  // ---------------------

  const DeliveryModule = (function(){
    let overlayEl = null;
    let userDismissedOnce = false;
    let widgetOpenedOnce = false;

    function buildOverlay(){
      if(overlayEl) return overlayEl;

      const ov = document.createElement("div");
      ov.id = "eit-delivery-cutoff-overlay";
      ov.style.position="fixed";
      ov.style.inset="0";
      ov.style.zIndex="2147483647";
      ov.style.background="rgba(0,0,0,0.55)";
      ov.style.display="none";
      ov.style.alignItems="center";
      ov.style.justifyContent="center";

      const card = document.createElement("div");
      card.style.background="#fffaf2";
      card.style.padding="28px 32px";
      card.style.borderRadius="12px";
      card.style.width="90%";
      card.style.maxWidth="680px";
      card.style.boxShadow="0 10px 40px rgba(0,0,0,0.35)";
      card.style.fontSize="16px";   // big font, same as old modal
      card.style.lineHeight="1.5";
      card.style.color="#7A4100";
      card.style.border="1px solid #F5D7A2";
      card.style.fontFamily="system-ui";

      const msg = document.createElement("div");
      msg.id = "eit-delivery-message";

      const btn = document.createElement("button");
      btn.innerText = "OK, I'll change it";
      btn.style.marginTop="18px";
      btn.style.padding="10px 18px";
      btn.style.background="#0b74de";
      btn.style.color="#fff";
      btn.style.borderRadius="6px";
      btn.style.border="none";
      btn.style.cursor="pointer";
      btn.style.fontSize="15px";

      btn.addEventListener("click", ()=>{
        ov.style.display="none";
        userDismissedOnce = true;
      });

      card.appendChild(msg);
      card.appendChild(btn);
      ov.appendChild(card);
      document.body.appendChild(ov);

      overlayEl = ov;
      return ov;
    }

    function showOverlay(data){
      const ov = buildOverlay();

      ov.querySelector("#eit-delivery-message").innerHTML = `
        <strong style="display:block; margin-bottom:8px; font-size:17px;">
          ⚠️ ${escapeHtml(data.restaurant)} Cut-off for Delivery on ${escapeHtml(data.date)}.
        </strong>
        Selected time 
          <span style="color:#c62828; font-weight:bold;">${escapeHtml(data.time)}</span> 
        is outside allowed range.
        <br>
        Please choose between 
          <span style="font-weight:bold;">${escapeHtml(data.window)}</span> so we can serve you fresh and on time.
      
    
      
      
          `;

      ov.style.display = "flex";
    }

    function hideOverlay(){
      if(overlayEl) overlayEl.style.display="none";
    }

    function evaluate(){
      if(getActiveMode() !== "delivery") return hideOverlay();

      try{
        const top = document.querySelector("[class*='TopBar'], #ZapietEats-TopBar");
        const text = top ? top.innerText : "";
        const lines = text.split("\n").map(x=>x.trim()).filter(Boolean);

        if(!lines.length || !lines[0].toLowerCase().includes("delivery")){
          hideOverlay(); return;
        }

        const byLine = lines.find(l=>l.startsWith("Delivery by:")) || "";
        const dateLine = lines[lines.length-1] || "";

        let restaurant = "";
        let m = byLine.match(/^Delivery by:\s*(.+?)(?:,|$)/i);
        if(m) restaurant = m[1].trim();

        let datePart="", timePart="";
        let dt = dateLine.match(/(.+?),\s*at\s*(\d{1,2}:\d{2}\s*(AM|PM))/i);
        if(dt){
          datePart = dt[1].trim();
          timePart = dt[2].trim();
        }

        const iso = parseISOFromLabel(datePart);
        if(!restaurant || !iso){ hideOverlay(); return; }

        const store = findStoreByName(restaurant);
        if(!store){ hideOverlay(); return; }

        const rule = store.rules?.find(r=>r.date===iso);
        if(!rule?.delivery){ hideOverlay(); return; }

        const dec = parseTimeDecimal(timePart);
        const open = Number(rule.delivery.open);
        const cutoff = Number(rule.delivery.cutoff);

        const outside = (dec==null || dec < open || dec > cutoff);

        if(outside && !userDismissedOnce){
          showOverlay({
            restaurant,
            date: datePart,
            time: timePart,
            window: `${formatTime(open)} and ${formatTime(cutoff)}`
          });

          if(!widgetOpenedOnce){
            const realBtn = document.querySelector(".ZapietEats-TopBar-Button");
            if(realBtn) realBtn.click();
            widgetOpenedOnce = true;
          }

        } else if(!outside){
          hideOverlay();
          userDismissedOnce = false;
          widgetOpenedOnce = false;
        }

      }catch(e){
        console.error("[EIT-DELIVERY] delivery eval err:",e);
      }
    }

    function formatTime(dec){
      const h = Math.floor(dec);
      const m = Math.round((dec-h)*60);
      const d = new Date();
      d.setHours(h,m);
      return d.toLocaleTimeString([], { hour:"numeric", minute:"2-digit" });
    }

    return { evaluate, hideOverlay };
  })();


  // ---------------------
  // Init watchers & mode handlers
  // ---------------------

  function initUnified(){
    PickupModule.initWatcher();

    function throttledRunner(){
      const mode = getActiveMode();
      if(mode === "pickup"){
        try { PickupModule.enforceCutoff(); } catch(e){}
      } else if(mode === "delivery"){
        try { DeliveryModule.evaluate(); } catch(e){}
      } else {
        try { PickupModule.enforceCutoff(); DeliveryModule.evaluate(); } catch(e){}
      }
    }

    let tick = null;
    function runThrottled(){
      clearTimeout(tick);
      tick = setTimeout(throttledRunner, 60);
    }

    new MutationObserver(runThrottled)
      .observe(document.body, { childList:true, subtree:true, characterData:true, attributes:true });

    window.addEventListener("zapieteats:submitted", ()=>{
      throttledRunner();
      setTimeout(throttledRunner, 120);
    }, false);

    onModeChange(() => { runThrottled(); });

    setTimeout(runThrottled, 200);
  }

  if(document.readyState === "loading") document.addEventListener("DOMContentLoaded", initUnified);
  else initUnified();

  window.EIT_CutoffUnified = {
    getActiveMode,
    Pickup: PickupModule,
    Delivery: DeliveryModule,
    stores
  };

})();
